import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Target, Loader2, Trash2, CheckCircle2, Clock, LogIn, TrendingUp } from "lucide-react";
import { Button } from "../ui/button";
import { api, formatApiErrorDetail } from "../../lib/game";
import { useAuth } from "../../context/AuthContext";

const OUTCOMES = [
  { key: "yes", label: "Yes", cls: "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/15" },
  { key: "partly", label: "Partly", cls: "border-yellow-500/50 text-yellow-300 hover:bg-yellow-500/15" },
  { key: "no", label: "Not yet", cls: "border-red-500/50 text-red-300 hover:bg-red-500/15" },
];

const outcomeLabel = (o) => ({ yes: "Done", partly: "Partly", no: "Not yet" }[o] || o);

const CheckinRow = ({ commitmentId, checkin, due, onDone }) => {
  const [saving, setSaving] = useState(false);
  const mark = async (outcome) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/commitments/${commitmentId}/checkin`, { day: checkin.day, outcome });
      onDone(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const done = checkin.status === "done";
  return (
    <div
      data-testid={`checkin-${checkin.day}`}
      className={`rounded-xl border px-3 py-2.5 ${
        done
          ? "border-slate-800 bg-slate-950/40"
          : due
          ? "border-teal-500/40 bg-teal-500/[0.07]"
          : "border-slate-800 bg-slate-950/30"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
          {done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Clock className="w-3.5 h-3.5 text-slate-500" />}
          Day {checkin.day} check-in
        </span>
        {done && (
          <span className="text-[11px] font-semibold text-slate-400">{outcomeLabel(checkin.outcome)}</span>
        )}
        {!done && !due && <span className="text-[11px] text-slate-500">Upcoming</span>}
      </div>
      {!done && due && (
        <div className="mt-2 flex gap-1.5" data-testid={`checkin-actions-${checkin.day}`}>
          {OUTCOMES.map((o) => (
            <button
              key={o.key}
              disabled={saving}
              data-testid={`checkin-${checkin.day}-${o.key}`}
              onClick={() => mark(o.key)}
              className={`flex-1 rounded-lg border text-xs font-semibold py-1.5 transition-colors bg-slate-950/40 ${o.cls} disabled:opacity-50`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const MyCommitments = ({ onSignIn, onNew }) => {
  const { user } = useAuth();
  const loggedIn = user && typeof user === "object";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/me/commitments")
      .then(({ data }) => setData(data))
      .catch(() => setData({ commitments: [], follow_through_rate: null }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loggedIn) load();
    else setLoading(false);
  }, [loggedIn, load]);

  const updateOne = (updated) => {
    setData((d) => ({
      ...d,
      commitments: d.commitments.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)),
    }));
  };

  const remove = async (id) => {
    try {
      await api.delete(`/commitments/${id}`);
      setData((d) => ({ ...d, commitments: d.commitments.filter((c) => c.id !== id) }));
      toast.success("Commitment removed");
    } catch (e) {
      toast.error("Couldn't remove commitment");
    }
  };

  if (!loggedIn) {
    return (
      <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-16">
        <div className="max-w-md mx-auto text-center rounded-3xl border border-slate-800 bg-slate-900/70 p-8">
          <Target className="w-10 h-10 text-teal-400 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-slate-100">My Commitments</h2>
          <p className="text-slate-400 text-sm mt-2">Sign in to save money commitments and track your follow-through over time.</p>
          <Button data-testid="commitments-signin-btn" onClick={onSignIn} className="mt-5 bg-slate-100 text-slate-900 hover:bg-white font-semibold">
            <LogIn className="w-4 h-4 mr-1.5" /> Sign In
          </Button>
        </div>
      </div>
    );
  }

  const commitments = data?.commitments || [];

  return (
    <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-8" data-testid="my-commitments-screen">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-50 flex items-center gap-2">
              <Target className="w-7 h-7 text-teal-400" /> My Commitments
            </h1>
            <p className="text-slate-400 text-sm mt-1">Small promises to your future self — check in as the days pass.</p>
          </div>
          {data?.follow_through_rate != null && (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-center shrink-0" data-testid="follow-through-rate">
              <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Follow-through
              </p>
              <p className="font-mono text-xl font-bold text-emerald-300">{data.follow_through_rate}%</p>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : commitments.length === 0 ? (
          <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-8 text-center">
            <p className="text-slate-300">You haven't made any commitments yet.</p>
            <p className="text-slate-500 text-sm mt-1">Finish a game and set one at the end to start building the habit.</p>
            <Button data-testid="commitments-play-btn" onClick={onNew} className="mt-5 bg-gradient-to-r from-emerald-500 to-teal-600 text-slate-950 font-bold">
              Play a game
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {commitments.map((c) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`commitment-${c.id}`}
                className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {c.source === "ai" && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-teal-300 border border-teal-500/40 bg-teal-500/10 rounded-full px-2 py-0.5">
                          AI suggested
                        </span>
                      )}
                      {c.status === "complete" && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 rounded-full px-2 py-0.5">
                          Completed
                        </span>
                      )}
                    </div>
                    <p className="text-slate-100 font-medium leading-snug">{c.text}</p>
                    {c.email_done_count > 0 && (
                      <p data-testid={`commitment-done-count-${c.id}`} className="mt-1 text-xs font-semibold text-emerald-300">
                        Done {c.email_done_count} time{c.email_done_count === 1 ? "" : "s"} — keep it going!
                      </p>
                    )}
                  </div>
                  <button
                    data-testid={`delete-commitment-${c.id}`}
                    onClick={() => remove(c.id)}
                    className="text-slate-600 hover:text-red-400 transition-colors shrink-0"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {(c.checkins || []).map((ck) => (
                    <CheckinRow
                      key={ck.day}
                      commitmentId={c.id}
                      checkin={ck}
                      due={(c.due_checkins || []).includes(ck.day)}
                      onDone={updateOne}
                    />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
